import React, { useEffect, useRef, useState } from "react";
import { FaTimes, FaPaperPlane } from "react-icons/fa";
import "./CatalogAssistant.css";

const welcomeMessage = {
  id: "welcome",
  role: "assistant",
  text: "Contame qué necesitás y para qué equipo. Busco en el catálogo y, si hace falta, en la web: compatibilidades, números de parte y fichas técnicas.",
};

const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[char]));

function markdownInline(value) {
  return escapeHtml(value)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(])\*([^*\s][^*]*)\*(?=[\s).,;:!?]|$)/g, "$1<em>$2</em>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
}

function markdownToHtml(value) {
  const lines = String(value ?? "").replace(/\r/g, "").split("\n");
  const output = [];
  const cells = (line) => line.trim().replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim());
  const isBullet = (line) => /^\s*[-*•]\s+/.test(line);
  const isNumbered = (line) => /^\s*\d+[.)]\s+/.test(line);
  const isTableHeader = (line, following = "") => line.includes("|")
    && /^\s*\|?\s*:?-{2,}/.test(following) && following.includes("|");
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) { index++; continue; }

    if (isTableHeader(line, lines[index + 1] || "")) {
      const headings = cells(line).map((cell) => `<th>${markdownInline(cell)}</th>`).join("");
      index += 2;
      let rows = "";
      while (index < lines.length && lines[index].includes("|") && lines[index].trim()) {
        rows += `<tr>${cells(lines[index]).map((cell) => `<td>${markdownInline(cell)}</td>`).join("")}</tr>`;
        index++;
      }
      output.push(`<div class="catalogAssistantTable"><table><thead><tr>${headings}</tr></thead><tbody>${rows}</tbody></table></div>`);
      continue;
    }

    const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (heading) {
      output.push(`<h4>${markdownInline(heading[1])}</h4>`);
      index++;
      continue;
    }

    if (isBullet(line) || isNumbered(line)) {
      const ordered = isNumbered(line);
      const matches = ordered ? isNumbered : isBullet;
      const items = [];
      while (index < lines.length && matches(lines[index])) {
        items.push(`<li>${markdownInline(lines[index].replace(/^\s*(?:[-*•]|\d+[.)])\s+/, ""))}</li>`);
        index++;
      }
      output.push(`<${ordered ? "ol" : "ul"}>${items.join("")}</${ordered ? "ol" : "ul"}>`);
      continue;
    }

    const paragraph = [];
    while (index < lines.length && lines[index].trim() && !isBullet(lines[index])
      && !isNumbered(lines[index]) && !/^\s*#{1,6}\s/.test(lines[index])
      && !isTableHeader(lines[index], lines[index + 1] || "")) {
      paragraph.push(markdownInline(lines[index]));
      index++;
    }
    output.push(`<p>${paragraph.join("<br>")}</p>`);
  }
  return output.join("");
}

const chatApiBase = (process.env.REACT_APP_URL_API_IA || "/ConsultasIA").replace(/\/+$/, "");
const chatApiUrl = `${chatApiBase}/api/chat.php`;

const CatalogAssistant = ({ searchTerm = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([welcomeMessage]);
  const [isSending, setIsSending] = useState(false);
  const historyRef = useRef([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      inputRef.current?.focus();
    }
  }, [isOpen, messages]);

  const startNewQuery = () => {
    if (isSending) return;
    historyRef.current = [];
    setMessages([welcomeMessage]);
    setDraft("");
    inputRef.current?.focus();
  };

  const sendText = async (value) => {
    const text = value.trim();
    if (!text || isSending) return;

    const nextHistory = [...historyRef.current, { rol: "usuario", texto: text }];
    historyRef.current = nextHistory;
    setMessages((current) => [...current, { id: `${Date.now()}-user`, role: "user", text }]);
    setDraft("");
    setIsOpen(true);
    setIsSending(true);

    try {
      const response = await fetch(chatApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mensajes: nextHistory }),
      });
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error(`Respuesta inválida del servidor (HTTP ${response.status})`);
      }
      if (!response.ok) throw new Error(data.error || `Error del servidor (HTTP ${response.status})`);

      const reply = data.texto || "No pude armar una respuesta. Probá reformular la consulta.";
      const productContext = (data.productos || []).map((product) => product.codigo).filter(Boolean);
      historyRef.current = [...nextHistory, {
        rol: "asistente",
        texto: reply + (productContext.length ? `\n\n(Productos recomendados: ${productContext.join(", ")})` : ""),
      }];
      setMessages((current) => [...current, {
        id: `${Date.now()}-assistant`, role: "assistant", text: reply,
        productos: data.productos || [], opciones: data.opciones || [], fuentes: data.fuentes || [],
      }]);
    } catch (error) {
      historyRef.current = nextHistory.slice(0, -1);
      setMessages((current) => [...current, {
        id: `${Date.now()}-error`, role: "assistant", text: error.message || "No se pudo conectar con el asistente.", error: true,
      }]);
    } finally {
      setIsSending(false);
    }
  };

  const sendMessage = (event) => {
    event.preventDefault();
    sendText(draft);
  };

  const askAboutSearch = () => {
    if (isSending) {
      setIsOpen(true);
      return;
    }
    const query = searchTerm.trim();
    if (!query) {
      setIsOpen(true);
      return;
    }
    if (historyRef.current.length) {
      historyRef.current = [];
      setMessages([]);
    }
    sendText(query);
  };

  return (
    <div className="catalogAssistantRoot">
      {isOpen ? (
        <section
          aria-label="Asistente de productos"
          className="catalogAssistantPanel"
        >
          <header className="catalogAssistantHeader">
            <div>
              <h2>Asistente</h2>
              <p className="text-sm text-gray-500">Busca en el catálogo y en la web</p>
            </div>
            <div className="catalogAssistantActions">
              <button
                type="button"
                onClick={startNewQuery}
                disabled={isSending}
                className="catalogAssistantNew"
              >
                Nueva consulta
              </button>
              <button
                type="button"
                aria-label="Cerrar asistente"
                onClick={() => setIsOpen(false)}
                className="catalogAssistantClose"
              >
                <FaTimes />
              </button>
            </div>
          </header>

          <div className="catalogAssistantMessages" aria-live="polite">
            {messages.map((message) => (
              <div key={message.id} className={`catalogAssistantEntry ${message.role === "user" ? "isUser" : "isAssistant"}`}>
                <div className={`catalogAssistantBubble${message.error ? " isError" : ""}`}>
                  {message.role === "assistant" && !message.error ? (
                    <div className="catalogAssistantMarkdown" dangerouslySetInnerHTML={{ __html: markdownToHtml(message.text) }} />
                  ) : message.text}
                </div>
                {message.productos?.length > 0 && (
                  <div className="catalogAssistantProducts">
                    {message.productos.map((product) => (
                      <a
                        key={product.codigo}
                        href={product.tienda || undefined}
                        target={product.tienda ? "_blank" : undefined}
                        rel={product.tienda ? "noopener noreferrer" : undefined}
                        className="catalogAssistantProduct"
                      >
                        <strong className="block">{product.descripcion}</strong>
                        <span className="text-gray-500">Cód. {product.codigo}{product.marca ? ` · ${product.marca}` : ""}</span>
                        {product.tienda && <span className="mt-1 block text-lime-700">Ver en la tienda ›</span>}
                      </a>
                    ))}
                  </div>
                )}
                {message.opciones?.length > 0 && (
                  <div className="catalogAssistantOptions">
                    {message.opciones.map((option) => (
                    <button key={option} type="button" onClick={() => sendText(option)} disabled={isSending}>
                        {option}
                      </button>
                    ))}
                  </div>
                )}
                {message.fuentes?.length > 0 && (
                  <details className="catalogAssistantSources">
                    <summary className="cursor-pointer">Fuentes</summary>
                    <ul className="ml-5 list-disc">
                      {message.fuentes.map((source) => (
                        <li key={source.url}><a className="text-blue-700 underline" href={source.url} target="_blank" rel="noopener noreferrer">{source.titulo || source.url}</a></li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))}
            {isSending && <div className="text-sm text-gray-500">Buscando en el catálogo y en la web…</div>}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="catalogAssistantForm">
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                event.currentTarget.style.height = "auto";
                event.currentTarget.style.height = `${Math.min(event.currentTarget.scrollHeight, 130)}px`;
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Escribí tu consulta..."
              aria-label="Escribí tu consulta"
            />
            <button
              type="submit"
              disabled={!draft.trim() || isSending}
            >
              <span>Enviar</span>
              <FaPaperPlane className="text-xs" />
            </button>
          </form>
        </section>
      ) : (
        <button
          type="button"
          onClick={askAboutSearch}
          className="catalogAssistantFloating"
        >
          Preguntarle al Asistente
        </button>
      )}
    </div>
  );
};

export default CatalogAssistant;
